/**
 * apiFetch, wrapper nad fetch, který automaticky přidá Supabase JWT
 * do hlavičky Authorization (když je uživatel přihlášený).
 *
 * Token posílají i výpočetní endpointy. Dřív je frontend volal holým
 * fetchem a backend je nechával otevřené, takže se placený report dal
 * vygenerovat zdarma přímo přes API a na účet klíče k Anthropicu.
 */
import { supabase } from './supabaseClient';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * Hlavička s tokenem, bez Content-Type.
 *
 * Pro volání, která si typ obsahu určují sama, hlavně FormData při
 * nahrávání PDF: kdyby se Content-Type nastavil ručně, prohlížeč by
 * nedoplnil hranici multipart zprávy a server by ji nepřečetl.
 */
export async function authHeaders(extra = {}) {
  const headers = { ...extra };
  if (supabase) {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function apiFetch(path, options = {}) {
  const headers = await authHeaders({
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  });
  return fetch(`${API_BASE}${path}`, { ...options, headers });
}

/** Uloží dokončenou valuaci do portálu (vyžaduje přihlášení). */
export async function saveValuation({ input, result, companyName, segment, tier = 'free', wizardState = null, reports = null }) {
  const res = await apiFetch('/api/valuations', {
    method: 'POST',
    body: JSON.stringify({
      input,
      result,
      company_name: companyName,
      segment,
      tier,
      status: 'completed',
      wizard_state: wizardState,
      reports,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Doplní/aktualizuje uloženou valuaci (reporty, wizard_state, tier). */
export async function updateValuation(id, { reports, wizardState, tier } = {}) {
  const body = {};
  if (reports !== undefined) body.reports = reports;
  if (wizardState !== undefined) body.wizard_state = wizardState;
  if (tier !== undefined) body.tier = tier;
  const res = await apiFetch(`/api/valuations/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Načte jednu valuaci (plný řádek vč. input_json s wizard_state a reporty). */
export async function getValuation(id) {
  const res = await apiFetch(`/api/valuations/${id}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Vrátí valuace přihlášeného uživatele (klient svoje, tým vše). */
export async function listValuations() {
  const res = await apiFetch('/api/valuations');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Zeptá se AI průvodce (pomoc s vyplněním). */
export async function askAssistant(question, context) {
  const res = await apiFetch('/api/assistant', {
    method: 'POST',
    body: JSON.stringify({ question, context }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Odešle žádost o konzultaci. */
export async function createConsultation(message) {
  const res = await apiFetch('/api/consultations', { method: 'POST', body: JSON.stringify({ message }) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Seznam konzultací (klient svoje, tým vše). */
export async function listConsultations() {
  const res = await apiFetch('/api/consultations');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Vlákno konzultace (zprávy). */
export async function getConsultationThread(id) {
  const res = await apiFetch(`/api/consultations/${id}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
  return res.json();
}

/** Poslat zprávu do vlákna (klient nebo tým). */
export async function postConsultationMessage(id, text) {
  const res = await apiFetch(`/api/consultations/${id}/messages`, { method: 'POST', body: JSON.stringify({ text }) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
  return res.json();
}

/** Eskalovat konzultaci na živého poradce. */
export async function escalateConsultation(id) {
  const res = await apiFetch(`/api/consultations/${id}/escalate`, { method: 'POST' });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
  return res.json();
}

/** Smaže valuaci. */
export async function deleteValuation(id) {
  const res = await apiFetch(`/api/valuations/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Seznam uživatelů (jen tým/admin). */
export async function listUsers() {
  const res = await apiFetch('/api/users');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Založí nového uživatele (jen admin). Vrací { email, temp_password, ... }. */
export async function createUser(payload) {
  const res = await apiFetch('/api/users', { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Změna role uživatele (jen admin). */
export async function setUserRole(userId, role) {
  const res = await apiFetch(`/api/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Jsou platby (Stripe) zapnuté? */
export async function getBillingConfig() {
  const res = await apiFetch('/api/billing/config');
  if (!res.ok) return { enabled: false };
  return res.json();
}

/** Stav plateb pro přihlášeného klienta vč. věrnostní slevy. */
export async function getBillingStatus() {
  const res = await apiFetch('/api/billing/status');
  if (!res.ok) return { enabled: false, loyalty: false, loyalty_percent: 30 };
  return res.json();
}

/** Na co už má přihlášený klient zaplaceno (seznam tierů). */
export async function getEntitlements(valuationId = null) {
  const q = valuationId ? `?valuation_id=${encodeURIComponent(valuationId)}` : '';
  const res = await apiFetch(`/api/billing/entitlements${q}`);
  if (!res.ok) return { tiers: [] };
  return res.json();
}

/** Spustí Stripe Checkout a přesměruje na platbu. */
export async function startCheckout(tier, valuationId = null) {
  const res = await apiFetch('/api/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ tier, valuation_id: valuationId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  return data;
}
